/**
 * KISSED University of Healing Arts & Sciences™ — public landing page.
 * Route: /university
 *
 * Programs are pulled from /api/university/options.catalog so the academic
 * structure can be managed from the admin Catalog Editor without code edits.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Leaf, FlaskConical, Sparkles, BookOpen, Award, ChevronRight } from "lucide-react";
import logo from "@/assets/brand/kissed-logo.png";
import { Button } from "@/components/ui/button";
import PathwayDiagram from "@/components/PathwayDiagram";
import UniversityTopNav from "@/components/UniversityTopNav";
import { Badge } from "@/components/ui/badge";
import { universityApi } from "@/lib/universityApi";

const DIVISION_ICON = {
  "College of Trichology Science": Sparkles,
  "College of Herbal Science": Leaf,
  "College of Holistic Health & Functional Wellness": FlaskConical,
  "College of Professional & Continuing Education": BookOpen,
};

export default function UniversityLanding() {
  const [catalog, setCatalog] = useState({ divisions: [] });
  const [moduleEnabled, setModuleEnabled] = useState(true);
  const [reviewWindow, setReviewWindow] = useState("3–5 business days");
  const [fee, setFee] = useState(50);

  useEffect(() => {
    universityApi.options().then((r) => {
      setCatalog(r.data.catalog || { divisions: [] });
      setModuleEnabled(!!r.data.module_enabled);
      setReviewWindow(r.data.review_window_days || "3–5 business days");
      setFee(r.data.application_fee_usd || 50);
    }).catch(() => setCatalog({ divisions: [] }));
  }, []);

  const activeDivisions = (catalog.divisions || []).filter((d) => d.active !== false);

  return (
    <div data-testid="university-landing" className="min-h-screen bg-gradient-to-b from-[#faf7f5] via-white to-[#f5f0ec]">
      <UniversityTopNav />

      <main className="max-w-6xl mx-auto px-6 py-16">
        {/* Hero */}
        <div className="max-w-3xl">
          <span className="text-xs uppercase tracking-[0.18em] text-purple-900 font-semibold">Admissions are open</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl text-slate-900 leading-tight">
            KISSED University of<br /> Healing Arts &amp; Sciences™
          </h1>
          <p className="mt-6 text-lg text-slate-700 leading-relaxed">
            Where clinical rigor meets sacred work. Train in <em>Trichology Science</em>, <em>Herbal Science</em>,
            and <em>Holistic Health &amp; Functional Wellness</em> with Dr.&nbsp;Christy&nbsp;Vonta and the
            KISSED faculty.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            {moduleEnabled ? (
              <>
                <Link to="/university/quiz">
                  <Button data-testid="university-quiz-cta" size="lg" className="bg-purple-900 hover:bg-purple-800 text-white px-7">
                    Take the Pathway Quiz
                  </Button>
                </Link>
                <Link to="/university/apply">
                  <Button data-testid="university-apply-cta" size="lg" variant="outline" className="border-purple-900 text-purple-900 hover:bg-purple-50 px-7">
                    Start your application
                  </Button>
                </Link>
                <Link to="/university/enroll">
                  <Button data-testid="university-enroll-cta" size="lg" variant="ghost" className="text-purple-900 hover:bg-purple-50 px-5">
                    Open enrollment <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </Link>
              </>
            ) : (
              <span className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2 text-sm text-amber-900">
                Admissions are temporarily paused. Please check back soon.
              </span>
            )}
          </div>
          <p className="mt-3 text-xs text-slate-500" data-testid="quiz-cta-helper">
            Not sure where to start? Answer 5 questions and receive a personalized program recommendation.
          </p>
        </div>

        {/* Visual pathway — Level I → IV */}
        <PathwayDiagram />

        {/* Divisions */}
        <section id="programs-of-study" className="mt-20 space-y-10" data-testid="programs-of-study">
          <div>
            <h2 className="font-display text-3xl text-slate-900">Programs of Study</h2>
            <p className="text-sm text-slate-600 mt-2 max-w-2xl">
              Students may enter at different levels based on qualifications and prior training.
              You will choose a <strong>Primary Program</strong> on your application, with an
              optional <strong>Secondary Program of Interest</strong>.
            </p>
          </div>

          {activeDivisions.map((division) => {
            const Icon = DIVISION_ICON[division.name] || GraduationCap;
            const activeLevels = (division.levels || []).filter((l) => l.active !== false);
            const activeTracks = (division.specialization_tracks || []).filter((t) => t.active !== false);
            return (
              <div
                key={division.key || division.name}
                data-testid={`division-${(division.key || division.name).replace(/\W+/g, "-").toLowerCase()}`}
                className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8"
              >
                <div className="flex items-start gap-4">
                  <div className="rounded-xl bg-purple-50 p-3 shrink-0">
                    <Icon className="w-7 h-7 text-purple-900" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-2xl text-slate-900">{division.name}</h3>
                    {division.description && (
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed">{division.description}</p>
                    )}
                  </div>
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-3">
                  {activeLevels.map((lvl) => (
                    <div
                      key={lvl.key || lvl.label}
                      data-testid={`level-${(lvl.key || lvl.label).replace(/\W+/g, "-").toLowerCase()}`}
                      className="rounded-xl border border-slate-200 bg-slate-50/60 p-4 hover:border-purple-300 transition"
                    >
                      <div className="flex items-center gap-2">
                        <Badge variant="secondary" className="font-mono text-[10px]">{lvl.label}</Badge>
                        <span className="font-medium text-slate-900 text-sm">{lvl.title}</span>
                      </div>
                      {lvl.description && (
                        <p className="mt-1 text-xs text-slate-600 leading-relaxed">{lvl.description}</p>
                      )}
                    </div>
                  ))}
                </div>

                {activeTracks.length > 0 && (
                  <div className="mt-6 rounded-xl border-2 border-dashed border-purple-300 bg-purple-50/40 p-4" data-testid={`tracks-${(division.key || "").toLowerCase()}`}>
                    <div className="text-xs uppercase tracking-wide text-purple-900 font-semibold mb-2 flex items-center gap-2">
                      <Award className="w-4 h-4" /> Specialization Track
                    </div>
                    {activeTracks.map((t) => (
                      <div key={t.label || t.key} className="mt-1">
                        <div className="font-display text-lg text-slate-900">{t.label}</div>
                        {t.description && (
                          <p className="text-xs text-slate-700 leading-relaxed mt-1">{t.description}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

        {/* How enrollment works — Wave 2 restructure: DTS is the only program requiring application */}
        <section className="mt-20 rounded-2xl border border-purple-100 bg-purple-50/40 p-6 sm:p-8" data-testid="how-enrollment-works">
          <h2 className="font-display text-2xl text-slate-900">How Enrollment Works</h2>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">
            Most KUHAS programs are <strong>open enrollment</strong> — no application, no waitlist, no review period.
          </p>
          <ol className="mt-4 space-y-3 text-sm text-slate-700 leading-relaxed list-decimal pl-5" data-testid="enrollment-steps">
            <li>
              Choose your program and payment plan:
              <ul className="mt-1 space-y-1 list-disc pl-5">
                <li><strong>Pay Tuition in Full</strong> — full program access immediately.</li>
                <li><strong>Pay Per Course</strong> — unlock courses one at a time. Each completed course includes a Certificate of Completion documenting instructional hours and earned credentials.</li>
              </ul>
            </li>
            <li>Pay securely through <strong>Square</strong> checkout.</li>
            <li>Access is granted <strong>immediately upon payment</strong> — start coursework the same day.</li>
          </ol>

          <div className="mt-6 border-t border-purple-100 pt-6">
            <h3 className="font-display text-lg text-slate-900">The Doctor of Trichology Science (DTS) program is the exception</h3>
            <p className="mt-2 text-sm text-slate-700 leading-relaxed">
              DTS requires formal application:
            </p>
            <ol className="mt-3 space-y-3 text-sm text-slate-700 leading-relaxed list-decimal pl-5" data-testid="dts-application-steps">
              <li>Complete your application and pay the <strong>non-refundable ${fee} application fee</strong> via secure Square checkout.</li>
              <li>Our Admissions Team reviews completed applications within <strong>{reviewWindow}</strong>.</li>
              <li>You&apos;ll receive an emailed decision: <strong>Accepted, Conditionally Accepted, Waitlisted, or Declined</strong>.</li>
              <li>If accepted, choose <strong>Pay Tuition in Full</strong> or <strong>Pay Per Credit Hour</strong> to begin coursework.</li>
              <li>All tuition and course fees must be paid before access to coursework is granted.</li>
            </ol>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <Link
              to="/university/enroll"
              className="inline-flex items-center justify-center rounded-full bg-purple-900 hover:bg-purple-800 text-white text-sm font-medium h-11 px-6"
              data-testid="enrollment-open-btn"
            >
              Begin Open Enrollment <ChevronRight className="w-4 h-4 ml-1" />
            </Link>
            {moduleEnabled && (
              <Link
                to="/university/apply"
                className="inline-flex items-center justify-center rounded-full border border-purple-900 text-purple-900 hover:bg-purple-50 text-sm font-medium h-11 px-6"
                data-testid="enrollment-dts-btn"
              >
                Start Your DTS Application <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}
